import {useEffect, useRef, useState} from "react";
import { useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {PenIcon, PlusIcon} from "../../components/svgicons/SvgIcons";
import FancyTooltip from "../../components/utils/FancyTooltip";
import FancyPaper from "../../components/FancyPaper";
import {useForm} from "react-hook-form";
import ResourceAccess from "../../components/security/ResourceAccess";
import {useApp} from "../../hooks/useApp";
import FancyDialogActions from "../../components/utils/FancyDialogActions";
import {
  getRequestListFamily,
  getRequestListSubFamily,
  getRequestListSubSubFamily,
  getRequestProductsInventory,
  getRequestTotal,
  getRequestWarehouse,
  uploadInventoryXml,
  validateInvoice,
} from "../../services/inventory/InventoryService";
import dayjs from "dayjs";
import AddOrUpdateInventoryXml from "../../components/utils/AddOrUpdateInventoryXml";
import {getRequestMeasureUnit} from "../../services/unit/UnitService";
import {Invoice, InvoiceProduct, MeasureUnit, MeasureUnitConvertion, Product} from "../../types/inventory/types";
import Swal from "sweetalert2";
import { formatNumber } from "../../components/utils/formatters";

const inputDesign = () => (
    {
      '& .MuiInputBase-root': {
        height: 40,
        fontSize: '0.875rem'
      },
      '& .MuiInputBase-input': {
        padding: '4px 8px'
      },
      '& input[type=number]': {
        MozAppearance: 'textfield'
      },
      '& input[type=number]::-webkit-outer-spin-button': {
        WebkitAppearance: 'none',
        margin: 0
      },
      '& input[type=number]::-webkit-inner-spin-button': {
        WebkitAppearance: 'none',
        margin: 0
      }
    }
);

type QuantityComponentProps = {
  row: Product;
  getDetailLineProductByUniqueId: (uniqueId: string) => Product;
  updateProductMeasureUnit: (row: Product, muc : MeasureUnitConvertion) => void;
}

const QuantityComponent = ({row, getDetailLineProductByUniqueId, updateProductMeasureUnit}: QuantityComponentProps) => {
  const productInTable = getDetailLineProductByUniqueId(row.uniqueId);
  const isConvertible = productInTable.convertibleMeasure === true;
  const measureName = productInTable.measureUnitConvertion?.name ?? productInTable.measure;
  const [inputValue, setInputValue] = useState<string>(() => {
    const q = productInTable.measureUnitConvertion?.quantity;
    return q === undefined ? '' : String(q);
  });

  useEffect(() => {
    const q = productInTable.measureUnitConvertion?.quantity;
    setInputValue(q === undefined ? '' : String(q));
  }, [productInTable.uniqueId, productInTable.measureUnitConvertion?.quantity]);

  const handleQuantityChange = (value: string) => {
    setInputValue(value);
    if (!isConvertible) return;
    if (value === '') return; // permitir vacío mientras se edita
    const numValue = parseFloat(value);
    if (!Number.isNaN(numValue) && numValue >= 0) {
      const roundedValue = Math.round(numValue * 100) / 100;
      updateProductMeasureUnit(productInTable, {name: measureName, quantity: roundedValue});
    }
  };

  const handleBlur = () => {
    if (!isConvertible) return;
    if (inputValue === '') {
      // Al salir del campo si está vacío, poner 0
      setInputValue('0');
      updateProductMeasureUnit(productInTable, {name: measureName, quantity: 0});
    }
  };

  if (isConvertible) {
    const charWidth = Math.max(4, inputValue.length + 2);
    return (
      <TextField
        size="small"
        type="number"
        variant="outlined"
        placeholder="Cantidad"
        value={inputValue}
        onChange={({target: {value}})=> handleQuantityChange(value)}
        onBlur={handleBlur}
        onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
        inputProps={{
          min: 0,
          step: 0.01,
          style: { textAlign: 'center' }
        }}
        sx={{
          ...inputDesign(),
          width: `${charWidth}ch`,
          minWidth: '100%',
        }}
        InputLabelProps={{ shrink: true }}
      />
    );
  }

  return (<span>{productInTable.quantity}</span>);
};

type MeasureUnitComponentProps = {
    row: Product;
    measureData: MeasureUnit[];
    getDetailLineProductByUniqueId: (uniqueId: string) => Product;
  updateProductMeasureUnit: (row: Product, muc : MeasureUnitConvertion) => void;
};

const MeasureUnitComponent = ({row, measureData, getDetailLineProductByUniqueId, updateProductMeasureUnit}: MeasureUnitComponentProps) => {
  const findMeasureUnit = (name: string | null): MeasureUnit => measureData.find(mu => mu.name === name) as MeasureUnit
  const [measureUnit, setMeasureUnit] = useState<MeasureUnit | undefined>(findMeasureUnit(row.measure));
  const productInTable = getDetailLineProductByUniqueId(row.uniqueId);
  if(productInTable.convertibleMeasure === true) {
    return (
        <Autocomplete
            options={measureData}
            getOptionLabel={({id, name}) => name}
            value={measureUnit}
            onChange={(_, newValue) => {
              let name: string | null = newValue?.name ?? null;
              setMeasureUnit(findMeasureUnit(name))
              const effectiveName = name ?? productInTable.measure;
              const qty = productInTable.measureUnitConvertion?.quantity ?? productInTable.quantity ?? 0;
              const measureUnitConvertion: MeasureUnitConvertion = { name: effectiveName, quantity: qty };
              updateProductMeasureUnit(productInTable, measureUnitConvertion);
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Seleccione"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={inputDesign()}
                />
            )}
        />
    );
  }
  return (<span>{row.measure}</span>);
};

const ManagementXml = () => {
  const navigate = useNavigate();
  const {
    authInfo,
    setLoading,
    setErrorMsg,
    modalData,
    setModalData,
    setSuccessMsg,
    errorMsg,
  } = useApp();
  const [inventoryData, setInventoryData] = useState<any>([]);
  const [FamilyData, setFamilyData] = useState<any>([]);
  const [WarehouseData, setWarehouseData] = useState<any>([]);
  const [measureData, setMeasureData] = useState<MeasureUnit[]>([]);
  const [TotalPrice, setTotalPrice] = useState<any>([]);
  const [formData, setFormData] = useState<any>("");
  const [page, setPage] = useState(0);
  const { handleSubmit } = useForm();
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [warehouseFilter, setWarehouseFilter] = useState<string>("");
  const [quantityFilter, setQuantityFilter] = useState<number | null>(null);
  const [quantityOperator, setQuantityOperator] = useState<any>("");
  const [showFilters, setShowFilters] = useState(false); const [selectedProductMap, setSelectedProductMap] = useState<{ [key: string]: any }>({});
  const [detailLineProducts, setDetailLineProducts] = useState<Product[]>([]);
  const [matchedProducts, setMatchedProducts] = useState<{ [key: string]: boolean }>({});
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [productModifications, setProductModifications] = useState<{ [key: string]: any }>({});
  const [invoiceData, setInvoiceData] = useState<Invoice | null>(null);
  const [measureUnitConvertion, setMeasureUnitConvertion] = useState<MeasureUnitConvertion | null>();
  const [currentRow, setCurrentRow] = useState<any>(null);

  const initForm = {
    user: "",
    id: 0,
    code: "",
    officialCode: "",
    serialCode: "",
    description: "",
    acquisitionDate: dayjs().format("YYYY-MM-DD"),
    availability: true,
    availableQuantity: 0,
    quantity: 0,
    brand: "",
    category: "",
    color: "",
    cost: 0,
    status: 0,
    warehouseLocation: "",
    notes: "",
    subSubFamily: {
      id: 0,
      name: "",
      code: "",
      description: "",
      subFamily: {
        id: 0,
        name: "",
        code: "",
        description: "",
        family: {
          id: 0,
          name: "",
          code: "",
          description: "",
        },
      },
    },
  };

  useEffect(() => {
    const dataInit = async () => {
      await handleFetchData(0, "");
      await Promise.all([
        getRequestListFamily("", 0, "200"),
        getRequestWarehouse(),
        getRequestTotal(),
        getRequestMeasureUnit()
      ]).then(([familyData, warehouses, totalPrice, measureUnits]) => {
        setFamilyData(familyData);
        setWarehouseData(warehouses);
        setTotalPrice(totalPrice);
        setMeasureData(measureUnits);
      });
    };
    dataInit();
  }, [warehouseFilter, statusFilter, quantityFilter, quantityOperator]);


  const handleFetchData = async (currentPage: number, filter: string) => {
    setLoading && setLoading(true);
    setPage(currentPage);

    try {
      let data = await getRequestProductsInventory(
        currentPage,
        showFilters ? "" : filter,
        5000
      );

      if (data) {
        setInventoryData(data);
      }

      setLoading && setLoading(false);
    } catch (error: any) {
      setLoading && setLoading(false);
      setErrorMsg && setErrorMsg(error.message);
    }
  };

  const handleFetchSubFamilyData = async (
    id: number,
    setSubfamily: React.Dispatch<React.SetStateAction<any[]>>
  ) => {
    try {
      let SubFamilydata = await getRequestListSubFamily(id, "", 0, "500");
      if (SubFamilydata && "content" in SubFamilydata) {
        setSubfamily(SubFamilydata.content as any[]);
      } else {
        setSubfamily([]);
      }
      setLoading && setLoading(false);
    } catch (error: any) {
      setLoading && setLoading(false);
      setErrorMsg && setErrorMsg(error.message);
    }
  };

  const handleFetchSubSubFamilyData = async (
    id: number,
    setSubSubfamily: React.Dispatch<React.SetStateAction<any[]>>
  ) => {
    try {
      let SubSubFamilydata = await getRequestListSubSubFamily(id, "", 0, "500");
      if (SubSubFamilydata && "content" in SubSubFamilydata) {
        setSubSubfamily(SubSubFamilydata.content as any[]);
      } else {
        setSubSubfamily([]);
      }
      setLoading && setLoading(false);
    } catch (error: any) {
      setLoading && setLoading(false);
      setErrorMsg && setErrorMsg(error.message);
    }
  };

  /**
   * Efecto para validar errores en caso de abrir modal
   */
  useEffect(() => {
    if (modalData && modalData?.modalOpen && errorMsg) {
      setModalData &&
        setModalData({
          modalOpen: false,
          modalType: "",
          modalObject: null,
        });
    }
  }, [modalData, errorMsg, setModalData]);
  /**
   * Evento de apertura de modal
   */
  const handleOpenModal = async (event: any) => {
    event.preventDefault();
    const modalAction = event.currentTarget.getAttribute("data-name");
    let object = null;
    const id = event.currentTarget.getAttribute("data-code");

    if (modalAction === "update") {
      // Buscar el producto en detailLineProducts
      const baseProduct = detailLineProducts.find((p: any) => p.uniqueId === id);
      
      // Si hay modificaciones guardadas, usar esas, sino usar el producto base
      object = productModifications[id] || baseProduct;
    }

    if (modalAction === "delete") {
      object = inventoryData.content.find((p: any) => p.id === parseInt(id));
    }

    //open modal
    setModalData &&
      setModalData({
        ...modalData,
        modalOpen: true,
        modalType: modalAction,
        modalObject: object,
      });
  };

  /**
   * Evento de cierre de modal
   * @param event
   */
  const handleCancelModal = () => {
    //@ts-ignore
    if (modalData?.modalType !== "delete") {
      setFormData(initForm);
    }
    setModalData &&
      setModalData({
        ...modalData,
        modalOpen: false,
        modalType: "",
        modalObject: null,
      });
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleOpenFileExplorer = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleCompleteInvoice = async () => {
    setLoading(true);
    try {
      try {
        // Validar que las cantidades no sean negativas o NaN. Vacías se tratan como 0.
        const hasInvalidQuantity = detailLineProducts.some((row) => {
          const q = row.convertibleMeasure === true
            ? row.measureUnitConvertion?.quantity
            : (row.creditQuantity ?? row.quantity);
          const quantity = q ?? 0;
          return quantity < 0 || Number.isNaN(quantity);
        });

        if (hasInvalidQuantity) {
          setErrorMsg("Las cantidades deben ser válidas y no negativas");
          setLoading(false);
          return;
        }

        let message: string;
        const detailLines: InvoiceProduct[] = detailLineProducts.map((row) => {
          const selectedId = selectedProductMap[row.uniqueId];
          let productData = row;
          let ticketCode: string | undefined;
          let ticketDescription: string | undefined;

          if (selectedId) {
            const selectedProduct = inventoryData.content.find((p: any) => p.id === selectedId);
            if (selectedProduct) {
              ticketCode = row.code;
              ticketDescription = row.description;
              productData = {
                ...row,
                code: selectedProduct.code,
                description: selectedProduct.description,
              };








              
            }
          }
          const {code, cost, description, measure, quantity, convertibleMeasure, measureUnitConvertion, creditQuantity} = productData;
          const actualQuantity = (creditQuantity ?? quantity ?? 0);
          const baseDetail = {code, cost, description, measure, quantity: actualQuantity, ticketCode, ticketDescription};

          if(convertibleMeasure === true){
            const safeQuantity = measureUnitConvertion?.quantity ?? 0;
            const safeMeasure = measureUnitConvertion?.name ?? measure;
            return {...baseDetail, measure: safeMeasure, quantity: safeQuantity};
          }
          return baseDetail;
        });
        let invoice: Invoice = {...invoiceData, detailLines, user: authInfo?.username} as Invoice;
        invoice = await validateInvoice(invoice);
        setInvoiceData(invoice);
        message = "Factura completada exitosamente";
        if(invoice.creditNote === true){
          message = "Nota de crédito completada exitosamente";
        }
        setSuccessMsg(message);
      } catch (validationError: any) {
        if (validationError.message.includes("previously added") ?? validationError.message.includes("ya fue agregada")) {
          setErrorMsg("Esta factura ya fue procesada anteriormente");
        } else {
          setErrorMsg("Error procesando la factura");
        }
        return;
      }

      await handleFetchData(0, "");
      setDetailLineProducts([]);
      setSelectedProductMap({});
      setProductModifications({});
      setInvoiceData(null);      navigate('/dashboard/invoices');    } catch (error: any) {
      setErrorMsg(error.message);
    } finally {
      setLoading(false);
    }
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLoading && setLoading(true);
    try {
      const response = await uploadInventoryXml(file);
      const invoice: Invoice = response?.invoice;
      // Limpiar modificaciones previas
      setProductModifications({});

      setInvoiceData(invoice);

      // Agregar un ID único a cada producto para poder distinguir duplicados
      const productsWithUniqueId = response?.detailLineProducts.map((product: any, index: number) => {
        if(product.convertibleMeasure === true){
          return {
            ...product,
            measureUnitConvertion: {
                name: product.measure,
                quantity: product.quantity
            },
            uniqueId: `${product.code}_${index}` // ID único basado en código + índice
          };
        }
        return {
          ...product,
          uniqueId: `${product.code}_${index}` // ID único basado en código + índice
        };
      }) ?? [];
      setDetailLineProducts(productsWithUniqueId);      // Verificar matches y actualizar matchedProducts
      const newMatchedProducts: { [key: string]: boolean } = {};

      productsWithUniqueId.forEach((product: any) => {
        const match = inventoryData.content.find(
          (p: any) => p.code === product.code
        );
        if (match) {
          newMatchedProducts[product.uniqueId] = true; // Usar uniqueId en lugar de code
        }
      });

      setMatchedProducts(newMatchedProducts); 
      
      // Configurar productos que coinciden automáticamente para notas de crédito
      if (invoice?.creditNote) {
        const newSelectedProductMap: { [key: string]: any } = {};
        productsWithUniqueId.forEach((product: any) => {
          if (newMatchedProducts[product.uniqueId]) {
            const match = inventoryData.content.find((p: any) => p.code === product.code);
            if (match) {
              newSelectedProductMap[product.uniqueId] = match.id;
            }
          }
        });
        setSelectedProductMap(newSelectedProductMap);
      }
      
      // Configurar productos que coinciden automáticamente, pero permitir reasignación
      if (response?.matchingProducts.length > 0) {
        const newSelectedProductMap: { [key: string]: any } = {};

        response?.matchingProducts.forEach((matchingProduct: any) => {
          const foundProduct = productsWithUniqueId.find(
            (product: any) => product.code === matchingProduct.code
          );

          if (foundProduct) {
            newSelectedProductMap[foundProduct.uniqueId] = matchingProduct.id;
          }
        });

        setSelectedProductMap(newSelectedProductMap);
      }
      if(invoice?.creditNote === true) {
        await Swal.fire({
          icon: 'info',
          title: 'Nota de crédito',
          text: `El archivo XML contiene una nota de crédito con la siguiente razón: ${invoice.reason ?? "Razón desconocida"}.`,
          confirmButtonText: 'Entendido'
        });
      }
    } catch (error: any) {
      if (error instanceof Error) {
        setErrorMsg && setErrorMsg(error.message);
      } else {
        setErrorMsg && setErrorMsg("Error desconocido");
      }
      resetDataOnPage();
    } finally {
      setLoading && setLoading(false);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };  const handleUpdateProductInTable = (updatedProduct: any) => {
    // Guardar las modificaciones en el estado separado
    setProductModifications(prev => ({
      ...prev,
      [updatedProduct.uniqueId]: updatedProduct
    }));

    setDetailLineProducts((prevProducts) =>
      prevProducts.map((product) =>
        product.uniqueId === updatedProduct.uniqueId ? {
          ...updatedProduct,
          uniqueId: product.uniqueId, // Mantener el uniqueId original
          // Asegurar que los campos requeridos no estén vacíos
          code: updatedProduct.code || product.code,
          description: updatedProduct.description || product.description,
          availableQuantity: updatedProduct.availableQuantity === undefined ? product.availableQuantity : updatedProduct.availableQuantity,
          // Conservar otros campos importantes
          acquisitionDate: updatedProduct.acquisitionDate || product.acquisitionDate,
          availability: updatedProduct.availability === undefined ? product.availability : updatedProduct.availability,
          brand: updatedProduct.brand || product.brand,
          category: updatedProduct.category || product.category,
          color: updatedProduct.color || product.color,
          cost: updatedProduct.cost === undefined ? product.cost : updatedProduct.cost,
          notes: updatedProduct.notes || product.notes,
          status: updatedProduct.status === undefined ? product.status : updatedProduct.status,
          subSubFamily: updatedProduct.subSubFamily || product.subSubFamily,
          warehouseLocation: updatedProduct.warehouseLocation || product.warehouseLocation
        } : product
      )
    );
    
    // Mostrar mensaje de éxito
    setSuccessMsg && setSuccessMsg("Producto actualizado correctamente");
  };

  const resetDataOnPage = () => {
    setInvoiceData(null);
    setDetailLineProducts([]);
  }

  const updateProductMeasureUnit = (row: Product, muc : MeasureUnitConvertion) => {
    const productIndex = getDetailLineProductIndexByUniqueId(row.uniqueId);
    const updatedProducts = [...detailLineProducts];
    const nextName: string = muc.name ?? row.measureUnitConvertion.name;
    const nextQuantity: number = muc.quantity ?? row.measureUnitConvertion.quantity ?? 0;
    updatedProducts[productIndex] = {
      ...row,
      measureUnitConvertion: {
        name: nextName,
        quantity: nextQuantity
      }
    };
    setDetailLineProducts(updatedProducts);
  }

  const getDetailLineProductByUniqueId = (uniqueId: string): Product => {
    return detailLineProducts.find((product) => product.uniqueId === uniqueId) as Product;
  }

  const getDetailLineProductIndexByUniqueId = (uniqueId: string): number => {
    return detailLineProducts.findIndex((product) => product.uniqueId === uniqueId);
  }
  return (
    <>
      <FancyPaper pagetitle="Carga de facturas" prevpage="Listado de facturas" linkprevpage="/dashboard/invoices">
        <Grid container spacing={8} sx={{ pb: 8, alignItems: "center" }}>
          <input
            type="file"
            accept=".xml"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
          />

          {/* Botón para agregar factura */}
          <Grid item lg={7} md={7} sm={5} xs={12}>
            <ResourceAccess isCode={true} pathOrCode={"XML:WRITE"}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenFileExplorer}
              >
                Agregar factura
                <PlusIcon sx={{ ml: 1 }} />
              </Button>
            </ResourceAccess>
          </Grid>
          <Grid item lg={5} md={5} sm={7} xs={12}>
          </Grid>
        </Grid>
        {
            invoiceData &&
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Typography
                  variant="h3"
                  sx={{
                    textAlign: "center",
                    fontSize: "1.50rem",
                    mb: 2,
                  }}
              >
                {invoiceData.creditNote === true ? `Nota de crédito` : `Factura electrónica`}
              </Typography>
            </Grid>
        }
        <TableContainer>
          <Table
            sx={{ minWidth: 900, textAlign: "center" }}
            aria-label="inventory table"
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: { md: '110px', lg: '120px' } }}>Código</TableCell>
                <TableCell align="center" sx={{ width: { md: '240px', lg: '270px' }, minWidth: '190px' }}>Descripción</TableCell>
                <TableCell align="center" sx={{ minWidth: '100px', whiteSpace: 'nowrap' }}>Cantidad</TableCell>
                <TableCell align="center" sx={{ width: { md: '200px', lg: '220px' }, minWidth: '180px' }}>Unidad de medida</TableCell>
                <TableCell sx={{ width: { xs: '200px', sm: '240px', md: '300px', lg: '320px' }, minWidth: '280px' }}>Seleccionar Producto</TableCell>
                <TableCell align="right" sx={{ width: { md: '130px', lg: '140px' }, minWidth: '110px' }}>Costo total</TableCell>
                <ResourceAccess isCode={true} pathOrCode={"XML:WRITE"}>
                  <TableCell align="center" sx={{ width: { md: '130px', lg: '140px' } }}>Acciones</TableCell>
                </ResourceAccess>
              </TableRow>
            </TableHead>
            <TableBody>
              {detailLineProducts.length > 0 ? (
                detailLineProducts.map((row: any) => {
                  return (
                      <TableRow key={row.uniqueId} sx={{ verticalAlign: 'middle' }}>
                        <TableCell sx={{ width: { md: '110px', lg: '120px' }, verticalAlign: 'middle', }}>{row.code}</TableCell>
                        <TableCell align="center" sx={{
                          width: { md: '240px', lg: '270px' },
                          maxWidth: { md: '290px', lg: '320px' },
                          minWidth: '190px',
                          whiteSpace: 'normal',
                          wordWrap: 'break-word',
                          wordBreak: 'break-word',
                          verticalAlign: 'middle',
                          padding: '8px'
                        }}>
                          {row.description}
                        </TableCell>
                        <TableCell align="center" sx={{ minWidth: '100px', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                          <QuantityComponent row={row}
                            getDetailLineProductByUniqueId={getDetailLineProductByUniqueId}
                            updateProductMeasureUnit={updateProductMeasureUnit}
                        /></TableCell>
                        <TableCell align="center" sx={{ width: { md: '200px', lg: '220px' }, minWidth: '200px', verticalAlign: 'top', whiteSpace: 'normal', wordWrap: 'break-word', wordBreak: 'break-word' }}>
                          <MeasureUnitComponent
                            row={row}
                            measureData={measureData}
                            getDetailLineProductByUniqueId={getDetailLineProductByUniqueId}
                            updateProductMeasureUnit={updateProductMeasureUnit}
                        /></TableCell>
                        <TableCell sx={{ width: { xs: '200px', sm: '240px', md: '300px', lg: '320px' }, minWidth: '280px', verticalAlign: 'top' }}>
                          <Autocomplete
                              options={inventoryData.content}
                              getOptionLabel={(product) =>
                                  `${product.description} (${product.code})`
                              }
                              value={
                                  inventoryData.content.find(
                                      (product: any) => product.id === selectedProductMap[row.uniqueId]
                                  ) ?? null
                              }
                              onChange={(event, newValue) => {
                                setSelectedProductMap((prev) => {
                                  const newSelected = { ...prev };
                                  setCurrentRow(null);
                                  if (newValue) {
                                    newSelected[row.uniqueId] = newValue.id;
                                  } else {
                                    delete newSelected[row.uniqueId];
                                  }
                                  return newSelected;
                                });
                              }}
                              renderInput={(params) => (
                                  <TextField
                                      {...params}
                                      label="Seleccionar producto"
                                      variant="outlined"
                                      fullWidth
                                      InputLabelProps={{
                                        shrink: true,
                                      }}
                                      sx={inputDesign()}
                                  />
                              )}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ width: { md: '130px', lg: '140px' }, minWidth: '110px', verticalAlign: 'middle' }}>
                          {formatNumber(row.cost ?? 0)}
                        </TableCell>
                        <TableCell align="center" sx={{ width: { md: '130px', lg: '140px' }, verticalAlign: 'top' }}>
                          <Checkbox
                              color="primary"
                              checked={selectedProductMap[row.uniqueId] !== undefined}
                              disabled={true}
                              onChange={(event) => {
                                const isChecked = event.target.checked;
                                setSelectedProductMap((prev) => {
                                  const newSelected = { ...prev };
                                  if (isChecked) {
                                    const foundProduct = inventoryData.content.find(
                                        (product: any) => product.code === row.code
                                    );
                                    if (foundProduct) {
                                      newSelected[row.uniqueId] = foundProduct.id;
                                    }
                                  } else {
                                    delete newSelected[row.uniqueId];
                                  }
                                  return newSelected;
                                });
                              }}
                          />
                          <FancyTooltip title="Editar" placement="top">
                            <IconButton
                                aria-label="edit"
                                component="label"
                                color="primary"
                                onClick={handleOpenModal}
                                data-name="update"
                                data-code={row.uniqueId}
                                disabled={selectedProductMap[row.uniqueId] !== undefined}
                            >
                              <PenIcon />
                            </IconButton>
                          </FancyTooltip>
                        </TableCell>
                      </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6}>No existen registros</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {detailLineProducts.length > 0 && (
            <>
              <Grid container justifyContent="flex-end" alignItems="center" sx={{ mt: 4, pr: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mr: 1 }}>
                  Costo Total:
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {formatNumber(
                    detailLineProducts.reduce((sum: number, row: any) => {
                      const lineCost = row.cost ?? 0;
                      return sum + lineCost;
                    }, 0)
                  )}
                </Typography>
              </Grid>
              <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setOpenConfirmDialog(true)}
                >
                  {invoiceData?.creditNote === true ? `Completar nota de crédito` : `Completar Factura`}
                </Button>
              </Grid>
            </>
          )}
          <Dialog
            open={openConfirmDialog}
            onClose={() => setOpenConfirmDialog(false)}
          >
            <DialogTitle>¿Estás seguro?</DialogTitle>
            <DialogContent>
              <Typography>
                ¿Deseas completar la factura? Esta acción no se puede revertir.
              </Typography>
            </DialogContent>
            <FancyDialogActions
              handleAccept={() => {
                handleCompleteInvoice();
                setOpenConfirmDialog(false);
              }}
              cancelModal={() => setOpenConfirmDialog(false)}
              textAcceptButton="Confirmar"
              textCancelButton="Cancelar"
            />
          </Dialog>
        </TableContainer>
      </FancyPaper>
      {(modalData?.modalType === "create" ||
        modalData?.modalType === "update") && (
          <Dialog
            open={modalData.modalOpen}
            onClose={handleCancelModal}
            maxWidth="md"
            fullWidth
          >
            <AddOrUpdateInventoryXml
              data={modalData?.modalObject}
              FamilyData={FamilyData}
              WarehouseData={WarehouseData}
              measureData={measureData}
              handleFetchSubFamilyData={handleFetchSubFamilyData}
              handleFetchSubSubFamilyData={handleFetchSubSubFamilyData}
              onSubmit={handleUpdateProductInTable}
              cancelModal={handleCancelModal}
              modalType={modalData?.modalType}
            />
          </Dialog>
        )}
    </>
  );
};

export default ManagementXml;
